import { Button } from "@/components/ui/baseUi/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/baseUi/dialog"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { useProgram } from "@/modules/programs/hooks/usePrograms"
import { useApplyWeekTemplate, useTemplate } from "@/modules/template/hooks/useTemplate"
import { word } from "@modules/core/utils/i18n"
import { ProgramType } from "@modules/programs/types/programTypes"
import { useProgramSessionEvents } from "@modules/training/hooks/useTraining"
import dayjs from "dayjs"
import { useImperativeHandle, useState } from "react"
import { appToast as toast, successToastClassName, errorToastClassName } from "@/components/ui/baseUi/sonner"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"

export type TemplateWeekImportModalRef = {
  onImportTemplateClick: () => Promise<void>
} & React.ComponentProps<"div">

export type TemplateWeekImportModalProps = {
  programId: string
  templateId: string
  options: {
    startDate: Date | null
    startIndex: number | null
  }
  ref: React.Ref<TemplateWeekImportModalRef>
  className?: string
}

export const TemplateWeekImportModal = ({
  programId,
  templateId,
  options,
  ref,
  className,
  ...props
}: TemplateWeekImportModalProps) => {
  const [showModal, setShowModal] = useState(false)
  const { hideSidebar } = useSidebar()
  const { mutateAsync: applyWeekTemplate, isPending: isApplyingWeekTemplate } = useApplyWeekTemplate()
  const { data: program } = useProgram(programId)
  const { data: template } = useTemplate(templateId)
  const {
    data: { sessions: programSessions },
  } = useProgramSessionEvents({
    programId: programId,
    programType: [ProgramType.VIRTUAL, ProgramType.PRO, ProgramType.ONOFF].includes(program.type)
      ? (program.type as ProgramType.VIRTUAL | ProgramType.PRO | ProgramType.ONOFF | undefined)
      : undefined,
    startDate: options.startDate ? dayjs(options.startDate).format("YYYY-MM-DD") : (options.startIndex ?? undefined),
    endDate: options.startDate
      ? dayjs(options.startDate).add(7, "day").format("YYYY-MM-DD")
      : options.startIndex
        ? options.startIndex + 6
        : undefined,
  })

  const currentWeekHasSessions = programSessions.length > 0

  const onHide = () => {
    setShowModal(false)
  }

  const onImportTemplate = async (overwrite: boolean) => {
    await applyWeekTemplate({
      templateId,
      applyWeekTemplateInput: {
        programId,
        startDate: options.startDate ? dayjs(options.startDate).format("YYYY-MM-DD") : null,
        startIndex: options.startIndex,
        overwrite,
      },
    })
      .then(() => {
        toast.success(word("template.week.toast.import.success"), {
          classNames: successToastClassName(),
        })
        hideSidebar()
        setShowModal(false)
      })
      .catch(() => {
        toast.error(word("template.week.toast.import.error"), {
          classNames: errorToastClassName(),
        })
      })
  }

  useImperativeHandle(ref, () => ({
    onImportTemplateClick: async () => {
      if (currentWeekHasSessions) {
        setShowModal(true)
      } else {
        await onImportTemplate(false)
      }
    },
  }))

  return (
    <Dialog open={showModal} onOpenChange={onHide}>
      <DialogContent className={cn("pt-15 pb-4 sm:max-w-md", className)} {...props}>
        <DialogHeader>
          <DialogTitle className="mt-4 flex w-full items-center justify-center">
            {/* HEADER */}
            <Text fontStyle="TL" className="text-center">
              {word("template.week.import.modale.title")}
            </Text>
          </DialogTitle>
        </DialogHeader>

        <DialogDescription>
          {/* CONTENT */}
          <Text fontStyle="BM" className="text-center wrap-break-word whitespace-break-spaces text-textDefault-caption">
            {word("template.week.import.modale.message", {
              templateName: template.name,
            })}
          </Text>
        </DialogDescription>

        <DialogFooter>
          {/* BUTTON */}
          <div className="mb-3 flex w-full flex-row items-center justify-center gap-4">
            <Button
              variant="outline"
              size="lg"
              onClick={() => void onImportTemplate(false)}
              disabled={isApplyingWeekTemplate}
              className="flex-1"
            >
              {word("program.weekCopyPaste.paste.modale.sessionsAlreadyExist.keep")}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={() => void onImportTemplate(true)}
              disabled={isApplyingWeekTemplate}
              className="flex-1"
            >
              {word("program.weekCopyPaste.paste.modale.sessionsAlreadyExist.replace")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
